import React, { useRef, useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Download, Hourglass, ArrowUp, ArrowDown, Minus } from 'lucide-react';
import {
  KpiLabel, KpiValue, KpiSub, ExportBtn, Skeleton,
} from '../styles';
import { KPI_ACCENTS } from '../constants';
import { exportCardPng } from '../exportCardPng';

const KpiGrid = styled.div`
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 16px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Tile = styled.div`
  position: relative;
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 1px 4px rgba(0, 66, 66, 0.06);
  border-left: 4px solid ${({ $accent }) => $accent};
  padding: 16px 18px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-height: 96px;
`;

const TileExport = styled(ExportBtn)`
  position: absolute;
  top: 8px;
  right: 8px;
`;

function KpiTile({ cardKey, accent, label, value, sub, loading }) {
  const { t } = useTranslation();
  const ref = useRef(null);
  const handleExport = useCallback(() => {
    exportCardPng(ref.current, cardKey).catch(() => {});
  }, [cardKey]);

  return (
    <Tile ref={ref} $accent={accent}>
      <TileExport
        type="button"
        data-export-exclude
        onClick={handleExport}
        title={t('leaveDashboard.export.saveAsPng')}
        aria-label={t('leaveDashboard.export.saveAsPng')}
      >
        <Download size={13} />
      </TileExport>
      <KpiLabel>{label}</KpiLabel>
      {loading ? <Skeleton $h="42px" /> : (
        <>
          <KpiValue>{value}</KpiValue>
          <KpiSub>{sub}</KpiSub>
        </>
      )}
    </Tile>
  );
}

export default function KpiRow({ summary, risks, balance }) {
  const { t, i18n } = useTranslation();
  const fmt = (v, digits = 1) => Number(v ?? 0).toLocaleString(i18n.language, { maximumFractionDigits: digits });

  const s = summary.data;
  const r = risks.data;
  const b = balance.data;

  const delta = s?.upcomingDelta ?? 0;
  const DeltaIcon = delta > 0 ? ArrowUp : delta < 0 ? ArrowDown : Minus;

  return (
    <KpiGrid>
      <KpiTile
        cardKey="kpi-bugun-izinde"
        accent={KPI_ACCENTS.onLeave}
        loading={summary.loading}
        label={t('leaveDashboard.kpi.onLeaveToday')}
        value={s?.onLeaveToday ?? 0}
        sub={t('leaveDashboard.kpi.ofHeadcount', { pct: fmt(s?.onLeavePercent) })}
      />
      <KpiTile
        cardKey="kpi-onay-kuyrugu"
        accent={KPI_ACCENTS.queue}
        loading={summary.loading}
        label={t('leaveDashboard.kpi.approvalQueue')}
        value={s?.pendingCount ?? 0}
        sub={(
          <>
            <Hourglass size={12} />
            {t('leaveDashboard.kpi.avgWaitDays', { days: fmt(s?.avgWaitDays) })}
          </>
        )}
      />
      <KpiTile
        cardKey="kpi-bu-hafta-cikacak"
        accent={KPI_ACCENTS.upcoming}
        loading={summary.loading}
        label={t('leaveDashboard.kpi.upcomingWeek')}
        value={s?.upcomingThisWeek ?? 0}
        sub={(
          <>
            <DeltaIcon size={12} />
            {t('leaveDashboard.kpi.vsLastWeek', { delta: `${delta > 0 ? '+' : ''}${fmt(delta, 0)}` })}
          </>
        )}
      />
      <KpiTile
        cardKey="kpi-risk-sinyali"
        accent={KPI_ACCENTS.risk}
        loading={risks.loading}
        label={t('leaveDashboard.kpi.riskSignals')}
        value={r ? r.burnoutCount + r.overlapCount : 0}
        sub={t('leaveDashboard.kpi.riskBreakdown', {
          burnout: fmt(r?.burnoutCount ?? 0, 0),
          overlap: fmt(r?.overlapCount ?? 0, 0),
        })}
      />
      <KpiTile
        cardKey="kpi-suresi-dolan-bakiye"
        accent={KPI_ACCENTS.expiring}
        loading={balance.loading}
        label={t('leaveDashboard.kpi.expiringBalances')}
        value={b?.expiringRiskCount ?? 0}
        sub={t('leaveDashboard.kpi.expiringWindow')}
      />
    </KpiGrid>
  );
}
